import { ref, readonly, computed } from 'vue'
import type { Survey } from '@/types/surveyTypes'
import * as yup from 'yup'

// Enum für Antwort-Ziele
export enum AnswerTarget {
  ANSWERS = 'answers',
  LEAD_DATA = 'leadData',
}

// Globaler State (außerhalb der Funktion) - wird von allen Instanzen geteilt
const surveyConfig = ref<Survey | null>(null)
const answers = ref<{ id: string; label: string; answer: any }[]>([])
const leadData = ref<{ id: string; answer: any }[]>([])
const currentStep = ref(0)
const stepsCount = computed(() => (surveyConfig.value ? surveyConfig.value.steps.length : 0))
const visibleQuestionIds = ref<Set<string>>(new Set())

export const useSurvey = () => {
  // Methoden die den globalen State verwalten
  const loadSurvey = (config: Survey) => {
    surveyConfig.value = { ...config } as Survey // Deep copy mit Type-Cast
  }

  // Hilfsfunktion für das Setzen von Antworten in einem Ref
  const updateAnswerInRef = (
    ref: any,
    questionId: string,
    answer: any,
    includeLabel = false,
    label = '',
  ) => {
    const existingIndex = ref.value.findIndex((a: any) => a.id === questionId)
    const answerData = includeLabel ? { id: questionId, label, answer } : { id: questionId, answer }

    if (existingIndex !== -1) {
      ref.value[existingIndex] = answerData
    } else {
      ref.value.push(answerData)
    }
  }

  const setAnswer = (
    questionId: string,
    label: string = '',
    value: any,
    target: AnswerTarget = AnswerTarget.ANSWERS,
  ) => {
    console.log(`Setze Antwort für Frage '${questionId}':`, value, `Ziel: ${target}`)

    switch (target) {
      case AnswerTarget.LEAD_DATA:
        updateAnswerInRef(leadData, questionId, value)
        break
      case AnswerTarget.ANSWERS:
      default:
        updateAnswerInRef(answers, questionId, value, true, label)
        break
    }
  }

  const goToStep = (stepIndex: number) => {
    if (stepIndex < 0 || stepIndex >= stepsCount.value) return
    currentStep.value = stepIndex
    // Bereinige Antworten für nicht-sichtbare Fragen beim Step-Wechsel
    cleanupInvisibleAnswers()
  }

  const createStepValidationSchema = (stepIndex: number) => {
    if (!surveyConfig.value || stepIndex >= surveyConfig.value.steps.length) {
      return yup.object({})
    }

    const step = surveyConfig.value.steps[stepIndex]

    const schema = createSchema(step)

    return yup.object(schema)
  }

  const createSchema = (step: any) => {
    const schema: Record<string, any> = {}

    step?.questions.forEach((question: any) => {
      // Nur sichtbare Fragen validieren
      if (!shouldShowQuestion(question)) {
        return
      }

      let validator: any

      switch (question.type) {
        case 'text':
          validator = yup.string().nullable()
          
          // Längen-Validierung
          if (question.validation?.min_length) {
            validator = validator.min(
              question.validation.min_length, 
              question.validation.message || `Mindestens ${question.validation.min_length} Zeichen`
            )
          }
          if (question.validation?.max_length) {
            validator = validator.max(
              question.validation.max_length, 
              question.validation.message || `Maximal ${question.validation.max_length} Zeichen`
            )
          }
          
          // Spezielle Validierungstypen
          if (question.validation?.type) {
            switch (question.validation.type) {
              case 'email':
                validator = validator.email(
                  question.validation.message || 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
                )
                break
              case 'url':
                validator = validator.url(
                  question.validation.message || 'Bitte geben Sie eine gültige URL ein'
                )
                break
              case 'phone':
                // Deutsche Telefonnummer-Regex
                const phoneRegex = /^[\+]?[\d\s\(\)\-\/]{6,20}$/
                validator = validator.matches(
                  phoneRegex,
                  question.validation.message || 'Bitte geben Sie eine gültige Telefonnummer ein'
                )
                break
              case 'regex':
                if (question.validation.pattern) {
                  try {
                    const regex = new RegExp(question.validation.pattern)
                    validator = validator.matches(
                      regex,
                      question.validation.message || 'Eingabe entspricht nicht dem erwarteten Format'
                    )
                  } catch (e) {
                    console.error('Invalid regex pattern:', question.validation.pattern)
                  }
                }
                break
            }
          }
          
          if (question.required) {
            validator = validator.required('Dieses Feld ist erforderlich')
          }
          break

        case 'number':
          validator = yup.number().nullable().typeError('Bitte geben Sie eine gültige Zahl ein')
          if (question.min !== undefined) {
            validator = validator.min(question.min, `Mindestens ${question.min}`)
          }
          if (question.max !== undefined) {
            validator = validator.max(question.max, `Maximal ${question.max} ${question.unit}`)
          }
          if (question.required) {
            validator = validator.required('Dieses Feld ist erforderlich')
          }
          break

        case 'single_choice':
          validator = yup.string().nullable()
          if (question.required) {
            validator = validator.required('Bitte eine Option wählen')
          }
          break

        case 'multi_choice':
          validator = yup.array()
          if (question.required) {
            validator = validator.min(1, 'Bitte mindestens eine Option wählen')
          }
          if (question.min_select) {
            validator = validator.min(
              question.min_select,
              `Mindestens ${question.min_select} Option(en) erforderlich`,
            )
          }
          if (question.max_select) {
            validator = validator.max(
              question.max_select,
              `Maximal ${question.max_select} Option(en) erlaubt`,
            )
          }
          break

        case 'slider':
          validator = yup.number().nullable().typeError('Bitte einen Wert wählen')
          if (question.min !== undefined) {
            validator = validator.min(question.min, `Mindestens ${question.min}`)
          }
          if (question.max !== undefined) {
            validator = validator.max(question.max, `Maximal ${question.max}`)
          }
          if (question.required) {
            validator = validator.required('Dieses Feld ist erforderlich')
          }
          break

        default:
          validator = yup.mixed().nullable()
          if (question.required) {
            validator = validator.required('Dieses Feld ist erforderlich')
          }
      }

      schema[question.id] = validator
    })

    return schema
  }

  const createFormValidationSchema = (questions: any[]) => {
    const tempStep = { questions }
    const schema = createSchema(tempStep)
    return yup.object(schema)
  }

  const getCurrentStepAnswers = () => {
    if (!surveyConfig.value) return {}

    const step = surveyConfig.value.steps[currentStep.value]
    const stepAnswers: Record<string, any> = {}

    step?.questions.forEach((question) => {
      const answer = answers.value.find((a) => a.id === question.id)
      stepAnswers[question.id] = answer?.answer || null
    })

    return stepAnswers
  }

  const shouldShowQuestion = (question: any) => {
    // Wenn keine Bedingung definiert ist, zeige die Frage immer
    if (!question.if_question || !question.if_answer) {
      return true
    }

    // Finde die Antwort auf die referenzierte Frage
    const conditionAnswer = answers.value.find((a) => a.id === question.if_question)

    if (!conditionAnswer || conditionAnswer.answer === null || conditionAnswer.answer === '') {
      return false
    }

    // Prüfe ob if_answer ein Array oder ein String ist
    if (Array.isArray(question.if_answer)) {
      // Array: Prüfe ob die Antwort in dem Array enthalten ist
      if (Array.isArray(conditionAnswer.answer)) {
        // Multi-Choice: Prüfe ob sich die Arrays überschneiden
        return question.if_answer.some((val: string) => conditionAnswer.answer.includes(val))
      } else {
        // Single-Choice: Prüfe ob der Wert im Array enthalten ist
        return question.if_answer.includes(conditionAnswer.answer)
      }
    } else {
      // String: Direkter Vergleich
      if (Array.isArray(conditionAnswer.answer)) {
        // Multi-Choice: Prüfe ob der String in der Antwort-Array enthalten ist
        return conditionAnswer.answer.includes(question.if_answer)
      } else {
        // Single-Choice: Direkter String-Vergleich
        return conditionAnswer.answer === question.if_answer
      }
    }
  }

  const cleanupInvisibleAnswers = () => {
    if (!surveyConfig.value) return

    // Sammle alle aktuell sichtbaren Fragen-IDs

    surveyConfig.value.steps.forEach((step) => {
      step.questions.forEach((question) => {
        if (shouldShowQuestion(question)) {
          visibleQuestionIds.value.add(question.id)
        }
      })
    })

    console.log('Sichtbare Fragen:', Array.from(visibleQuestionIds.value))
    console.log(
      'Aktuelle Antworten:',
      answers.value.map((a) => a.id),
    )

    // Entferne Antworten für nicht-sichtbare Fragen
    const oldAnswersCount = answers.value.length
    answers.value = answers.value.filter((answer) => visibleQuestionIds.value.has(answer.id))

    console.log(`Cleanup: ${oldAnswersCount} -> ${answers.value.length} Antworten`)
  }

  return {
    surveyConfig: surveyConfig, // Read-only für Komponenten
    answers: readonly(answers),
    leadData: readonly(leadData),
    visibleQuestionIds,
    currentStep,
    stepsCount,
    setAnswer,
    loadSurvey,
    goToStep,
    createStepValidationSchema,
    createFormValidationSchema,
    getCurrentStepAnswers,
    shouldShowQuestion,
    cleanupInvisibleAnswers,
  }
}
